// Theme settings — the merchant's `settings.theme_settings` object: brand
// colours, logo and every banner list. One parser for all three payload
// shapes the app receives (store-resolve response, account, org).
//
// Fields used by the storefront:
//   primary_color, secondary_color, background_color, text_color
//   logo_url | logo.url
//   loading (url string or { url })          — custom loader image (optional)
//   hero_banners (legacy: banners)            — hero carousel
//   sub_banners  (legacy: sub_banner)         — tiles under the hero
//   promotion_banners                         — wide promo strip
//   sub_promotion_banners                     — secondary promo strip
//   enable_hero_banners, enable_sub_banners, enable_promotion_banners,
//   enable_sub_promotion_banners              — per-list on/off (default on)
// Banner object shape: see ./banners.js.
import { resolveThemeColors } from '@core/utils/themeColors';

export const DEFAULT_LOGO_URL = '/pwa-192x192.png';

const list = (...candidates) => candidates.find(Array.isArray) ?? [];

/** The four admin banner lists, raw (each item: { image, title, description, button_text, button_link }). */
export function getBannerLists(theme) {
  const source = theme || {};
  const isEnabled = (v) => v !== false && v !== 'false' && v !== 0;
  return {
    hero: list(source.hero_banners, source.banners),
    sub: list(source.sub_banners, source.sub_banner),
    promotion: list(source.promotion_banners),
    subPromotion: list(source.sub_promotion_banners),
    appHero: list(source.app_hero_banners, source.app_hero_banner),
    appSub: list(source.app_sub_banners, source.app_sub_banner),
    appPromotion: list(source.app_promotion_banners, source.app_promotion_banner),
    appSubPromotion: list(source.app_sub_promotion_banners, source.app_sub_promotion_banner),
    enableHero: isEnabled(source.enable_hero_banners),
    enableSub: isEnabled(source.enable_sub_banners),
    enablePromotion: isEnabled(source.enable_promotion_banners),
    enableSubPromotion: isEnabled(source.enable_sub_promotion_banners),
  };
}

const parseFeatures = (candidate) =>
  Array.isArray(candidate)
    ? candidate.filter(
        (item) =>
          item &&
          (item.title || item.description || item.image?.url || typeof item.image === 'string'),
      )
    : [];

/**
 * @param theme            raw theme_settings
 * @param logoFallbacks    extra logo URLs to try (account/brand logos) before the default
 */
export function parseThemeSettings(theme, { logoFallbacks = [] } = {}) {
  const source = theme || {};
  const logoUrl =
    [source.logo_url, source.logo?.url, ...logoFallbacks].find(Boolean) || DEFAULT_LOGO_URL;
  return {
    primaryColor: source.primary_color || null,
    secondaryColor: source.secondary_color || null,
    backgroundColor: source.background_color || null,
    textColor: source.text_color || null,
    logoUrl,
    loaderUrl: (typeof source.loading === 'string' ? source.loading : source.loading?.url) || null,
    banners: getBannerLists(source),
    features: parseFeatures(source.features),
  };
}

/**
 * Shape stored in useRuntimeThemeStore (kept flat for existing readers).
 * `ecommerce` is the raw ecommerce_settings object.
 */
export function toRuntimeThemeState(parsedTheme, ecommerce) {
  const ecommerceFeatures = parseFeatures(ecommerce?.features);

  return {
    primaryColor: parsedTheme.primaryColor,
    secondaryColor: parsedTheme.secondaryColor,
    backgroundColor: parsedTheme.backgroundColor,
    textColor: parsedTheme.textColor,
    logoUrl: parsedTheme.logoUrl,
    loaderUrl: parsedTheme.loaderUrl,
    banners: parsedTheme.banners.hero,
    subBanners: parsedTheme.banners.sub,
    subPromotionBanners: parsedTheme.banners.subPromotion,
    promotionBanners: parsedTheme.banners.promotion,
    appBanners: parsedTheme.banners.appHero || [],
    appSubBanners: parsedTheme.banners.appSub || [],
    appPromotionBanners: parsedTheme.banners.appPromotion || [],
    appSubPromotionBanners: parsedTheme.banners.appSubPromotion || [],
    enableHeroBanners: parsedTheme.banners.enableHero,
    enableSubBanners: parsedTheme.banners.enableSub,
    enablePromotionBanners: parsedTheme.banners.enablePromotion,
    enableSubPromotionBanners: parsedTheme.banners.enableSubPromotion,
    ecommerceSettings: ecommerce || null,
    features: parsedTheme.features?.length > 0 ? parsedTheme.features : ecommerceFeatures,
  };
}

/** Writes the merchant colours to the `--runtime-color-*` CSS variables. */
export function applyThemeCssVars(colors) {
  if (typeof document === 'undefined') return;
  const resolved = resolveThemeColors(colors);
  const vars = {
    '--runtime-color-primary': resolved.primary,
    '--runtime-color-secondary': resolved.secondary,
    '--runtime-color-background': resolved.background,
    '--runtime-color-text': resolved.text,
    '--runtime-color-primary-hover': resolved.primaryHover,
    '--runtime-color-primary-active': resolved.primaryActive,
    '--runtime-color-primary-soft': resolved.primarySoft,
    '--runtime-color-border': resolved.border,
    '--runtime-color-card-bg': resolved.cardBackground,
    '--runtime-color-input-border': resolved.inputBorder,
    '--runtime-color-icon': resolved.icon,
    '--runtime-color-text-muted': resolved.mutedText,
    '--runtime-color-background-text': resolved.text,
  };
  const root = document.documentElement;
  Object.entries(vars).forEach(([name, value]) => {
    if (value) root.style.setProperty(name, value);
    else root.style.removeProperty(name);
  });
}
